import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  PrimaryBlueButton,
  SecondaryBlueButton,
  LoadingSpinner
} from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Schedule {
  id: number;
  reportType: string;
  frequency: string;
  recipientEmails: string;
  isEnabled: boolean;
  updatedAt: string;
}

type FrequencyKey = "daily" | "weekly" | "bi-monthly" | "monthly";

// ── Static config ─────────────────────────────────────────────────────────────

const REPORT_TYPES: { key: string; label: string; description: string }[] = [
  {
    key: "account-overview",
    label: "Account Overview",
    description: "Agents, teams, subscription features"
  },
  {
    key: "conversations",
    label: "Conversations",
    description: "Chat volume and resolution breakdown"
  },
  {
    key: "missed-chats",
    label: "Missed Chats",
    description: "Unanswered and missed engagement counts"
  },
  {
    key: "agent-performance",
    label: "Agent Performance",
    description: "Ratings, handle times, CSAT scores"
  },
  {
    key: "agent-availability",
    label: "Agent Availability",
    description: "Online / away / offline time tracking"
  },
  {
    key: "agent-utilization",
    label: "Agent Utilization",
    description: "Concurrent chats and capacity usage"
  },
  {
    key: "response-time",
    label: "Response Time",
    description: "First response and resolution time metrics"
  },
  {
    key: "sentiment",
    label: "Sentiment",
    description: "Positive / negative / neutral chat sentiment"
  }
];

const FREQUENCIES: { value: FrequencyKey; label: string; hint: string }[] = [
  { value: "daily",      label: "Daily",      hint: "Sent each morning (yesterday's data)" },
  { value: "weekly",     label: "Weekly",     hint: "Sent every Monday (prior 7 days)" },
  { value: "bi-monthly", label: "Bi-monthly", hint: "Sent 1st & 16th of each month" },
  { value: "monthly",    label: "Monthly",    hint: "Sent on the 1st (prior full month)" }
];

// ── Helper ────────────────────────────────────────────────────────────────────

function buildScheduleMap(schedules: Schedule[]): Record<string, Schedule> {
  const map: Record<string, Schedule> = {};
  for (const s of schedules) {
    map[s.reportType] = s;
  }
  return map;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ScheduledReports() {
  // On-demand
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [emailRecipients, setEmailRecipients] = useState("");
  const [generating, setGenerating] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [onDemandMsg, setOnDemandMsg] = useState<string | null>(null);
  const [onDemandError, setOnDemandError] = useState<string | null>(null);

  // Schedules
  const [scheduleMap, setScheduleMap] = useState<Record<string, Schedule>>({});
  const [schedulesLoading, setSchedulesLoading] = useState(true);

  // Per-row draft state: { [reportType]: { emails, frequency } }
  const [draft, setDraft] = useState<
    Record<string, { emails: string; frequency: FrequencyKey }>
  >({});

  // Per-row save status
  const [rowStatus, setRowStatus] = useState<
    Record<string, { saving: boolean; msg: string | null; err: string | null }>
  >({});

  const api = useApi();

  // ── Load schedules ──────────────────────────────────────────────────────────
  const loadSchedules = useCallback(async () => {
    setSchedulesLoading(true);
    try {
      const res = await api.admin.get("Reports/Schedules");
      const data = await res.json();
      const list: Schedule[] = Array.isArray(data) ? data : [];
      const map = buildScheduleMap(list);
      setScheduleMap(map);

      // Seed draft from existing schedules
      const newDraft: typeof draft = {};
      for (const rt of REPORT_TYPES) {
        const existing = map[rt.key];
        newDraft[rt.key] = {
          emails: existing?.recipientEmails ?? "",
          frequency: (existing?.frequency as FrequencyKey) ?? "monthly"
        };
      }
      setDraft(newDraft);
    } catch {
      setScheduleMap({});
    } finally {
      setSchedulesLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  // ── On-Demand: Download ─────────────────────────────────────────────────────
  async function handleDownload() {
    setGenerating(true);
    setOnDemandMsg(null);
    setOnDemandError(null);
    try {
      const res = await api.admin.post("Reports/Generate", { startDate, endDate });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Unknown error" }));
        setOnDemandError(err?.error ?? "Report generation failed.");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") ?? "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] ?? "velaro-report.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      setOnDemandMsg("Report downloaded.");
    } catch {
      setOnDemandError("Network error. Please try again.");
    } finally {
      setGenerating(false);
    }
  }

  // ── On-Demand: Email ────────────────────────────────────────────────────────
  async function handleEmail() {
    if (!emailRecipients.trim()) {
      setOnDemandError("Please enter at least one email address.");
      return;
    }
    setEmailing(true);
    setOnDemandMsg(null);
    setOnDemandError(null);
    try {
      const res = await api.admin.post("Reports/Email", {
        startDate,
        endDate,
        recipientEmails: emailRecipients
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setOnDemandMsg(data?.message ?? "Report emailed.");
      } else {
        setOnDemandError(data?.error ?? "Failed to send report.");
      }
    } catch {
      setOnDemandError("Network error. Please try again.");
    } finally {
      setEmailing(false);
    }
  }

  // ── Schedule: Toggle enabled/disabled ───────────────────────────────────────
  async function handleToggle(reportType: string, enabled: boolean) {
    const existing = scheduleMap[reportType];
    const { emails, frequency } = draft[reportType] ?? {
      emails: "",
      frequency: "monthly" as FrequencyKey
    };

    setRowStatus((prev) => ({
      ...prev,
      [reportType]: { saving: true, msg: null, err: null }
    }));

    try {
      const res = await api.admin.put("Reports/Schedules", {
        reportType,
        frequency: existing?.frequency ?? frequency,
        recipientEmails: existing?.recipientEmails ?? emails,
        isEnabled: enabled
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setRowStatus((prev) => ({
          ...prev,
          [reportType]: { saving: false, msg: enabled ? "Enabled." : "Disabled.", err: null }
        }));
        loadSchedules();
      } else {
        setRowStatus((prev) => ({
          ...prev,
          [reportType]: { saving: false, msg: null, err: data?.error ?? "Failed to update." }
        }));
      }
    } catch {
      setRowStatus((prev) => ({
        ...prev,
        [reportType]: { saving: false, msg: null, err: "Network error." }
      }));
    }
  }

  // ── Schedule: Save row (emails + frequency) ─────────────────────────────────
  async function handleSaveRow(reportType: string) {
    const { emails, frequency } = draft[reportType] ?? {
      emails: "",
      frequency: "monthly" as FrequencyKey
    };

    if (!emails.trim()) {
      setRowStatus((prev) => ({
        ...prev,
        [reportType]: { saving: false, msg: null, err: "Enter at least one recipient email." }
      }));
      return;
    }

    const existing = scheduleMap[reportType];

    setRowStatus((prev) => ({
      ...prev,
      [reportType]: { saving: true, msg: null, err: null }
    }));

    try {
      const res = await api.admin.put("Reports/Schedules", {
        reportType,
        frequency,
        recipientEmails: emails,
        isEnabled: existing?.isEnabled ?? true
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setRowStatus((prev) => ({
          ...prev,
          [reportType]: { saving: false, msg: "Saved.", err: null }
        }));
        loadSchedules();
      } else {
        setRowStatus((prev) => ({
          ...prev,
          [reportType]: { saving: false, msg: null, err: data?.error ?? "Failed to save." }
        }));
      }
    } catch {
      setRowStatus((prev) => ({
        ...prev,
        [reportType]: { saving: false, msg: null, err: "Network error." }
      }));
    }
  }

  function updateDraft(
    reportType: string,
    field: "emails" | "frequency",
    value: string
  ) {
    setDraft((prev) => ({
      ...prev,
      [reportType]: {
        ...prev[reportType],
        [field]: value
      }
    }));
    // Clear status when user edits
    setRowStatus((prev) => ({
      ...prev,
      [reportType]: { saving: false, msg: null, err: null }
    }));
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8 max-w-3xl">
      {/* On-Demand Section */}
      <section>
        <h2 className="text-lg font-semibold text-slate-800 mb-1">
          Generate On-Demand Report
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Download an Excel report for your account or email it to specified
          recipients.
        </p>

        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400"
              />
            </div>
          </div>

          <PrimaryBlueButton
            label={generating ? "Generating…" : "Download Excel"}
            onClick={handleDownload}
            disabled={generating || emailing}
          />

          <hr className="border-slate-200" />

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Email recipients{" "}
              <span className="text-slate-400 font-normal">(comma-separated)</span>
            </label>
            <input
              type="text"
              placeholder="you@company.com, colleague@company.com"
              value={emailRecipients}
              onChange={(e) => setEmailRecipients(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400 mb-3"
            />
            <SecondaryBlueButton
              label={emailing ? "Sending…" : "Send via Email"}
              onClick={handleEmail}
              disabled={generating || emailing}
            />
          </div>

          {onDemandMsg && (
            <p className="text-sm text-green-600 bg-green-50 border border-green-200 rounded px-3 py-2">
              {onDemandMsg}
            </p>
          )}
          {onDemandError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {onDemandError}
            </p>
          )}
        </div>
      </section>

      {/* Scheduled Reports Section */}
      <section>
        <h2 className="text-lg font-semibold text-slate-800 mb-1">
          Scheduled Reports
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Enable automatic email delivery for each report type. Reports are sent
          at <strong>6 AM UTC</strong> on the schedule you choose.
        </p>

        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
          {schedulesLoading ? (
            <div className="p-6 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            REPORT_TYPES.map((rt) => {
              const existing = scheduleMap[rt.key];
              const isEnabled = existing?.isEnabled ?? false;
              const rowDraft = draft[rt.key] ?? { emails: "", frequency: "monthly" };
              const status = rowStatus[rt.key] ?? {
                saving: false,
                msg: null,
                err: null
              };

              return (
                <div key={rt.key} className="p-4">
                  {/* Row header: toggle + name */}
                  <div className="flex items-center gap-3 mb-3">
                    <button
                      role="switch"
                      aria-checked={isEnabled}
                      aria-label={`${isEnabled ? "Disable" : "Enable"} ${rt.label} report`}
                      onClick={() => handleToggle(rt.key, !isEnabled)}
                      disabled={status.saving}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400 ${
                        isEnabled ? "bg-cornflower-blue-500" : "bg-slate-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          isEnabled ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        {rt.label}
                      </p>
                      <p className="text-xs text-slate-400">{rt.description}</p>
                    </div>
                  </div>

                  {/* Editable fields — always visible so admins can pre-configure */}
                  <div className="ml-12 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Recipient emails */}
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Recipients{" "}
                          <span className="text-slate-400 font-normal">
                            (comma-separated)
                          </span>
                        </label>
                        <input
                          type="text"
                          placeholder="email@company.com"
                          value={rowDraft.emails}
                          onChange={(e) =>
                            updateDraft(rt.key, "emails", e.target.value)
                          }
                          className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400"
                        />
                      </div>

                      {/* Frequency */}
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Frequency
                        </label>
                        <select
                          value={rowDraft.frequency}
                          onChange={(e) =>
                            updateDraft(
                              rt.key,
                              "frequency",
                              e.target.value as FrequencyKey
                            )
                          }
                          className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400"
                        >
                          {FREQUENCIES.map((f) => (
                            <option key={f.value} value={f.value}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {
                            FREQUENCIES.find(
                              (f) => f.value === rowDraft.frequency
                            )?.hint
                          }
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <SecondaryBlueButton
                        label={status.saving ? "Saving…" : "Save"}
                        onClick={() => handleSaveRow(rt.key)}
                        disabled={status.saving}
                      />
                      {existing?.updatedAt && (
                        <span className="text-xs text-slate-400">
                          Last saved {existing.updatedAt}
                        </span>
                      )}
                    </div>

                    {status.msg && (
                      <p className="text-xs text-green-600 bg-green-50 border border-green-200 rounded px-2.5 py-1.5">
                        {status.msg}
                      </p>
                    )}
                    {status.err && (
                      <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2.5 py-1.5">
                        {status.err}
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
